import React from 'react';

import { FormDropdown } from './FormDropdown';
import { US_REGIONS } from '../../constants/regions';

import type { StyleProp, ViewStyle } from 'react-native';

interface RegionSelectorProps {
  label: string;
  value: string;
  onSelect: (region: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnlyMode?: boolean;
  error?: string;
  helperText?: string;
  containerStyle?: StyleProp<ViewStyle>;
  /** Forwarded to `FormDropdown` — `2` shows two lines in the closed trigger (label + subtitle). */
  triggerTextLines?: 1 | 2;
  searchable?: boolean;
  searchPlaceholder?: string;
  optionSubtitles?: Record<string, string>;
  allowClear?: boolean;
  testID?: string;
}

export const RegionSelector: React.FC<RegionSelectorProps> = ({
  label,
  value,
  onSelect,
  placeholder = 'Select region',
  required = false,
  disabled = false,
  readOnlyMode = false,
  error,
  helperText,
  containerStyle,
  triggerTextLines = 1,
  searchable,
  searchPlaceholder,
  optionSubtitles,
  allowClear = false,
  testID,
}) => {
  const regionOptions = US_REGIONS.map(region => region.label);

  // Match case-insensitively: stored region values may arrive uppercase
  // (e.g. OCR-imported drafts save 'NAM' per the Bedrock prompt's allowedValues),
  // while US_REGIONS canonicalizes to lowercase. Compare on a normalized form
  // so either casing resolves to the right option.
  const normalizedValue = (value ?? '').toLowerCase();
  const current = US_REGIONS.find(
    region => region.value.toLowerCase() === normalizedValue,
  );
  const displayValue = current ? current.label : '';

  const handleSelect = (selectedLabel: string) => {
    if (selectedLabel === '') {
      onSelect('');
      return;
    }
    const found = US_REGIONS.find(region => region.label === selectedLabel);
    if (found) {
      onSelect(found.value);
    }
  };

  return (
    <FormDropdown
      label={label}
      value={displayValue}
      options={regionOptions}
      onSelect={handleSelect}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      readOnlyMode={readOnlyMode}
      error={error}
      helperText={helperText}
      containerStyle={containerStyle}
      triggerTextLines={triggerTextLines}
      searchable={searchable}
      searchPlaceholder={searchPlaceholder}
      optionSubtitles={optionSubtitles}
      allowClear={allowClear}
      testID={testID}
    />
  );
};
